import { Disposable } from "./Disposable";

/**
 * Helper class to manage access to a stack of disposables.
 *
 * When disposed, it will dispose all disposables in the stack.
 * This is useful when you have to allocate a lot of resources across
 * a lot of different functions, and you want to automatically clean
 * up everything whey you're done.
 *
 * @example
 * ```
 * const lifecycle = new Lifecycle();
 *
 * // somewhere deep inside the call stack that receives
 * // lifecycle as an argument
 * function onTick() {
 *  console.log("Tick");
 * }
 * window.addEventListener("tick", onTick);
 * lifecycle.push(() => window.removeEventListener("tick", onTick));
 * // ...
 *
 * lifecycle.dispose(); // cleans up everything at once
 * ```
 */
export class Lifecycle implements Disposable {
    private disposables: Disposable[] = [];

    /**
     * Push a callback/{@link Disposable} to be called when this Lifecycle is disposed.
     */
    public push(disposable: Disposable | (() => void)) {
        if (typeof disposable === "function") {
            disposable = { dispose: disposable };
        }
        this.disposables.push(disposable);
    }
    public dispose() {
        this.disposables.forEach((d) => d.dispose());
        this.disposables = [];
    }
}
